import React, { useEffect } from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import ko from "date-fns/locale/ko"
import styled from "styled-components"
import { Button, COLOR_SYSTEM, Input, SHADOW, Text } from "components/atoms"

interface DateRangePickerStyledProps {
  disabled: boolean
  isStartAtError?: boolean
  isEndAtError?: boolean
  isBannerMode?: boolean
}

const DateRangePickerLayout = styled.div<DateRangePickerStyledProps>`
  display: flex;
  align-items: center;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};

  .datepicker__range-text {
    margin: 0 12px;
  }

  .react-datepicker {
    width: 250px;
    padding: 24px;
    border: none;
    border-radius: 20px;
    font-family: 1.2rem;;
    ${SHADOW.get("4")};
  }

  .react-datepicker__month-container {
    width: 100%;
  }

  .react-datepicker__header {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
    border-bottom: none;
  }

  .react-datepicker__navigation {
    width: 20px;
    height: 20px;
    top: 31px;
  }

  .react-datepicker__navigation--previous {
    left: 24px;
  }

  .react-datepicker__navigation--next {
    right: 24px;
  }

  .react-datepicker__current-month {
    color: #000;
    font-size: 1.3rem;
    font-weight: 700;
    margin-top: 0;
    margin-bottom: 0.8rem;
    /* margin-bottom: 12px; */

  }

  .react-datepicker__day-name {
    display: inline-block;
    margin: 0.6rem;
    line-height: 1.7rem;
    text-align: center;
    /* width: 1.7rem; */
    font-size: 1.3rem;
    font-weight: 700;

    color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  }

  .react-datepicker__month {
    margin: 0.4rem 0;
    font-size: 1.2rem;
  }

  .react-datepicker__week {
    display: flex;
  }

  .react-datepicker__day {
    height: 30px;
    line-height: 30px;
    flex: 1 1 33%;
    color: ${COLOR_SYSTEM.get("Gray")?.[900]};
    margin: 0;
    border-radius: 0;
  }

  .react-datepicker__day:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[15]};
  }

  .react-datepicker__day--disabled {
    color: #ccc;
  }

  .react-datepicker__day--in-range {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[15]};
  }

  .react-datepicker__day--in-selecting-range {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[15]};
    border-radius: 0;
  }

  .react-datepicker__day--in-selecting-range:not(
      .react-datepicker__day--in-range
    ) {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[15]};
    border-radius: 0;
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--range-start {
    color: ${COLOR_SYSTEM.get("Gray")?.[0]};
    background-color: ${COLOR_SYSTEM.get("Skyblue")?.[400]};
    border-radius: 50%;
  }

  .react-datepicker__input-container * {
    border: ${({ disabled }) => (disabled ? "none" : "")};
    cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
    /* background-color: ${COLOR_SYSTEM.get("Gray")?.[10]}; */
  }

  .react-datepicker__input-container .banner-datepicker-start * {
    background-color: ${({ isStartAtError }) =>
      isStartAtError
        ? COLOR_SYSTEM.get("Red")?.[10]
        : COLOR_SYSTEM.get("Gray")?.[10]};
  }

  .react-datepicker__input-container .banner-datepicker-end * {
    background-color: ${({ isEndAtError }) =>
      isEndAtError
        ? COLOR_SYSTEM.get("Red")?.[10]
        : COLOR_SYSTEM.get("Gray")?.[10]};
  }

  .banner-datepicker-end {
    border: ${({ isEndAtError }) =>
      isEndAtError
        ? `1px solid ${COLOR_SYSTEM.get("Red")?.[100]}`
        : ``};
    border-radius: 12px;
  }
  .banner-datepicker-start {
    border: ${({ isStartAtError }) =>
      isStartAtError
        ? `1px solid ${COLOR_SYSTEM.get("Red")?.[100]}`
        : ``};
    border-radius: 12px;
  }

  .datepicker-end {
    border: ${({ isEndAtError }) =>
      isEndAtError
        ? `1px solid ${COLOR_SYSTEM.get("Red")?.[400]}`
        : `1px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`};
    border-radius: 12px;
  }
  .datepicker-start {
    border: ${({ isStartAtError }) =>
      isStartAtError
        ? `1px solid ${COLOR_SYSTEM.get("Red")?.[400]}`
        : `1px solid ${COLOR_SYSTEM.get("Gray")?.[50]}`};
    border-radius: 12px;
  }

  .datepicker-end:focus-within *,
  .datepicker-start:focus-within * {
    /* border: 1px solid ${COLOR_SYSTEM.get("Skyblue")?.[400]}; */
    background-color: ${({ disabled }) =>
      disabled
        ? COLOR_SYSTEM.get("Gray")?.[10]
        : COLOR_SYSTEM.get("Gray")?.[50]};
  }
`

export type DateKind = "start" | "end"
interface DateRangePickerProps {
  startDate: Date | string
  endDate: Date | string
  disabled: boolean
  error?: boolean
  onChangeDate: (name: DateKind, date: Date) => void
  isStartAtError?: number
  isEndAtError?: number
  isBannerMode?: boolean
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  disabled,
  error,
  onChangeDate,
  isStartAtError,
  isEndAtError,
  isBannerMode,
}) => {
  return (
    <DateRangePickerLayout
      disabled={disabled}
      isStartAtError={isStartAtError}
      isEndAtError={isEndAtError}
    >
      <DatePicker
        className="datepicker-start"
        locale={ko}
        dateFormat={"yyyy/MM/dd"}
        dateFormatCalendar={"yyyy년 MM월"}
        selected={
          typeof startDate === "string" ? new Date(startDate) : startDate
        }
        onChange={(date: Date) => onChangeDate("start", date)}
        selectsStart
        showPopperArrow={false}
        // minDate={new Date()}
        startDate={
          typeof startDate === "string" ? new Date(startDate) : startDate
        }
        endDate={typeof endDate === "string" ? new Date(endDate) : endDate}
        disabled={disabled}
        placeholderText="시작일"
        customInput={
          <Input
            rightItems={[
              <Button
                key={""}
                color="Quaternary"
                size="Medium"
                variant="Ghost"
                icon="Calendar Day"
                type="Icon"
              />,
            ]}
            error={error}
            backgroundColor={COLOR_SYSTEM.get("Gray")[0]}
            readonly
          />
        }
      />
      <Text
        textType="InputLarge"
        color={COLOR_SYSTEM.get("Gray")?.[800]}
        className="datepicker__range-text"
      >
        ~
      </Text>
      <DatePicker
        className="datepicker-end"
        locale={ko}
        dateFormat={"yyyy/MM/dd"}
        dateFormatCalendar={"yyyy년 MM월"}
        selected={typeof endDate === "string" ? new Date(endDate) : endDate}
        onChange={(date: Date) => onChangeDate("end", date)}
        selectsEnd
        showPopperArrow={false}
        startDate={
          typeof startDate === "string" ? new Date(startDate) : startDate
        }
        endDate={typeof endDate === "string" ? new Date(endDate) : endDate}
        minDate={
          typeof startDate === "string" ? new Date(startDate) : startDate
        }
        disabled={disabled}
        placeholderText="마감일"
        customInput={
          <Input
            rightItems={[
              <Button
                key={""}
                color="Quaternary"
                size="Medium"
                variant="Ghost"
                icon="Calendar Day"
                type="Icon"
              />,
            ]}
            backgroundColor={COLOR_SYSTEM.get("Gray")[0]}
            error={error}
            readonly
          />
        }
        onKeyDown={(e) => {
          e.preventDefault()
        }}
      />
    </DateRangePickerLayout>
  )
}

const BannerDateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  disabled,
  error,
  onChangeDate,
  isStartAtError,
  isEndAtError,
  isBannerMode,
}) => {
  return (
    <DateRangePickerLayout
      disabled={disabled}
      isStartAtError={isStartAtError}
      isEndAtError={isEndAtError}
    >
      <DatePicker
        className="banner-datepicker-start"
        locale={ko}
        dateFormat={"yyyy/MM/dd"}
        dateFormatCalendar={"yyyy년 MM월"}
        selected={
          typeof startDate === "string" ? new Date(startDate) : startDate
        }
        onChange={(date: Date) => onChangeDate("start", date)}
        selectsStart
        showPopperArrow={false}
        minDate={new Date()}
        startDate={
          typeof startDate === "string" ? new Date(startDate) : startDate
        }
        endDate={typeof endDate === "string" ? new Date(endDate) : endDate}
        disabled={disabled}
        placeholderText="시작일"
        customInput={
          <Input
            rightItems={[
              <Button
                key={""}
                color="Quaternary"
                size="Medium"
                variant="Ghost"
                icon="Calendar Day"
                type="Icon"
              />,
            ]}
            error={error}
            backgroundColor={COLOR_SYSTEM.get("Gray")[0]}
            readonly
          />
        }
      />
      <Text
        textType="InputLarge"
        color={COLOR_SYSTEM.get("Gray")?.[800]}
        className="datepicker__range-text"
      >
        ~
      </Text>
      <DatePicker
        className="banner-datepicker-end"
        locale={ko}
        dateFormat={"yyyy/MM/dd"}
        dateFormatCalendar={"yyyy년 MM월"}
        selected={typeof endDate === "string" ? new Date(endDate) : endDate}
        onChange={(date: Date) => onChangeDate("end", date)}
        selectsEnd
        showPopperArrow={false}
        startDate={
          typeof startDate === "string" ? new Date(startDate) : startDate
        }
        endDate={typeof endDate === "string" ? new Date(endDate) : endDate}
        minDate={
          typeof startDate === "string" ? new Date(startDate) : startDate
        }
        disabled={disabled}
        placeholderText="마감일"
        customInput={
          <Input
            rightItems={[
              <Button
                key={""}
                color="Quaternary"
                size="Medium"
                variant="Ghost"
                icon="Calendar Day"
                type="Icon"
              />,
            ]}
            backgroundColor={COLOR_SYSTEM.get("Gray")[0]}
            error={error}
            readonly
          />
        }
        onKeyDown={(e) => {
          e.preventDefault()
        }}
      />
    </DateRangePickerLayout>
  )
}

export {DateRangePicker, BannerDateRangePicker}
